import React from "react";
import {action, observable, computed, makeObservable} from "mobx";

export class UiState {
    @observable activeTool: string;
    @observable language: string;
    @observable menuVisible: boolean;
    @observable pendingRequestCount: number;
    @observable popupDisabled: boolean;
    @observable popupVisible: boolean;
    @observable sidebarVisible: boolean;
    @observable sidebarHigh: boolean;
    @observable topicswitcherVisible: boolean;
    @observable layerlistProcessing: boolean;
    @observable widgets = observable.map<string, React.Component>();

    /**
     * App window size
     */
    @observable uiWidth: number | undefined;
    @observable uiHeight: number | undefined;
    @observable isSmallDevice: boolean | undefined;

    /**
     * Remember selected tab per station
     */
    @observable selectedTabs = observable.map<number, number>();

    /**
     * Resizing state of a popup window
     */
    @observable resizing = observable.box(false);
    @observable toolGraphic: __esri.Graphic | null = null;
    sublayerMap = new Map<string, string>();
    constructor() {
        makeObservable(this);
        this.setActiveTool("");
        this.setLanguage("de-CH");
        this.resetPendingRequestCount();
        this.setPopupDisabled(false);
        this.setPopupVisible(false);
        this.setSidebarVisible(false);
        this.setTopicswitcherVisible(false);
        this.handleWindowResizeWhenSidebarOpen = this.handleWindowResizeWhenSidebarOpen.bind(this);
    }

    @computed get appIsInSync() {
        return this.pendingRequestCount === 0;
    }
    @computed
    get isSidebarVisible() {
        return this.sidebarVisible;
    }

    @action
    addWidget(key: string, widget: React.Component) {
        this.widgets.set(key, widget);
    }

    @action
    setActiveTool(tool: string, graphic?: __esri.Graphic | null) {
        this.setToolGraphic(graphic);
        this.activeTool = tool;
    }

    @action
    setLanguage(lang: string) {
        this.language = lang;
    }
    @action
    setLayerlistProcessing(processing: boolean) {
        this.layerlistProcessing = processing;
    }

    @action
    setUiSize(width: number | undefined, height: number | undefined, isSmallDevice?: boolean) {
        if(typeof width === "number" && typeof height === "number") {
            this.uiWidth = width;
            this.uiHeight = height;
            this.isSmallDevice = isSmallDevice;
        }
    }

    @action
    resetPendingRequestCount() {
        this.pendingRequestCount = 0;
    }

    @action
    removeWidget(key: string) {
        this.widgets.delete(key);
    }

    @action
    setMenuVisible(visible: boolean) {
        this.menuVisible = visible;
    }
    @action
    setTopicswitcherVisible(visible: boolean) {
        this.topicswitcherVisible = visible;
    }
    @action
    setPopupDisabled(disabled: boolean) {
        this.popupDisabled = disabled;
    }
    @action
    setPopupVisible(visible: boolean) {
        this.popupVisible = visible;
    }

    @action
    setSidebarVisible(visible: boolean) {
        if (visible) {
            // add listener when menu open
            window.addEventListener("resize", this.handleWindowResizeWhenSidebarOpen);
        } else {
            window.removeEventListener("resize", this.handleWindowResizeWhenSidebarOpen);
        }
        this.sidebarVisible = visible;
    }
    @action
    setToolGraphic(toolGraphic?: __esri.Graphic | null) {
        if(typeof toolGraphic === "undefined"){
            toolGraphic = null;
        }
        this.toolGraphic = toolGraphic;
    }
    @action
    toggleSidebarVisible() {
        this.setSidebarVisible(!this.sidebarVisible);
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleWindowResizeWhenSidebarOpen(event: any) {
        if (this.isSidebarVisible) {
            this.setSidebarVisible(false);
        }
        // only resize once
        window.removeEventListener("resize", this.handleWindowResizeWhenSidebarOpen);
    }
    @action
    setSidebarHigh(isHigh: boolean){
        this.sidebarHigh = isHigh;
    }
    /**
     * Start resizing
     */
    startResizing() {
        this.resizing.set(true);
    }

    /**
     * Stop resizing
     */
    stopResizing() {
        this.resizing.set(false);
    }

    isResizing(): boolean {
        return this.resizing.get();
    }

}
